import React, { useRef } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Download, Award, Calendar, Hash } from 'lucide-react';

const Certificate = ({ certificateData, onDownload }) => {
  const certificateRef = useRef(null);

  const handleDownload = () => {
    // Convert certificate to image for download
    if (certificateRef.current) {
      // Use html2canvas or similar library for PDF generation
      // For now, we'll trigger the parent download function
      onDownload && onDownload();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCourseFullName = (courseType) => {
    return courseType === 'foundation' 
      ? 'Foundation Video Book Review Specialist'
      : 'Professional Video Book Review Specialist (Pro)';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="p-0 shadow-2xl">
        <div 
          ref={certificateRef}
          className="bg-gradient-to-br from-blue-50 to-indigo-50 p-12"
          style={{ aspectRatio: '11.7/8.3' }} // A4 landscape ratio
        >
          {/* Certificate Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/bupoexyc_book%20review%20specialist%20small%20logo.jpg"
                alt="Book Review Specialist Logo"
                className="h-16 w-auto mr-4"
              />
              <div className="text-left">
                <h1 className="text-2xl font-bold text-slate-800">
                  Book Review Specialists
                </h1>
                <p className="text-sm text-slate-600">Professional Certification Program</p>
              </div>
            </div>
          </div>

          {/* Certificate Title */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-blue-700 mb-2">
              Certificate of Completion
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto mb-4"></div>
            <p className="text-lg text-slate-600">
              This is to certify that
            </p>
          </div>

          {/* Student Name */}
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-slate-800 border-b-2 border-slate-300 pb-2 inline-block min-w-96">
              {certificateData.student_name}
            </h3>
          </div>

          {/* Course Details */}
          <div className="text-center mb-8">
            <p className="text-lg text-slate-700 mb-2">
              has successfully completed the
            </p>
            <h4 className="text-2xl font-bold text-blue-700 mb-4">
              {getCourseFullName(certificateData.course_type)}
            </h4>
            <p className="text-base text-slate-600">
              and is now qualified to operate as a certified video book review specialist
            </p>
          </div>

          {/* Certificate Details Grid */}
          <div className="grid grid-cols-3 gap-8 mb-8 text-center">
            <div>
              <div className="flex items-center justify-center mb-2">
                <Hash className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-semibold text-slate-700">Certificate ID</span>
              </div>
              <p className="font-mono text-lg font-bold text-slate-800 bg-white px-3 py-1 rounded border">
                {certificateData.certificate_id}
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-center mb-2">
                <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-semibold text-slate-700">Completion Date</span>
              </div>
              <p className="text-lg font-bold text-slate-800">
                {formatDate(certificateData.completion_date)}
              </p>
            </div>
            
            <div>
              <div className="flex items-center justify-center mb-2">
                <Award className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-semibold text-slate-700">CPD Provider</span>
              </div>
              <p className="text-lg font-bold text-slate-800">
                #{certificateData.cpd_provider}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-end">
            <div className="text-left">
              <div className="w-48 border-b-2 border-slate-400 mb-2"></div>
              <p className="text-sm text-slate-600">
                <strong>Diane Shawe</strong><br />
                Founder & Director<br />
                Book Review Specialists
              </p>
            </div>
            
            <div className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/whbb2v24_Cpd%20ACCREDITED_video%20book%20review%20course.gif"
                alt="CPD Accredited"
                className="w-16 h-16 mx-auto mb-2"
              />
              <p className="text-xs text-slate-600">CPD Accredited</p>
            </div>
            
            <div className="text-right">
              <p className="text-xs text-slate-500 mb-2">
                Issued: {formatDate(new Date().toISOString())}
              </p>
              <p className="text-xs text-slate-500">
                Verify at: bookreviewspecialists.app
              </p>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Download Button */}
      <div className="text-center mt-6">
        <Button onClick={handleDownload} className="btn btn-primary">
          <Download className="w-4 h-4 mr-2" />
          Download Certificate
        </Button>
      </div>
    </div>
  );
};

export default Certificate;